import { useCallback, useEffect, useState } from "react";
import { ScrollText, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveRoomEvents } from "@/hooks/useLiveRoomWS";
import { ROLE_STYLE } from "@/lib/liveRoles";

const ROLE_TXT = { host: "text-yellow-300", admin: ROLE_STYLE.admin.name, moderator: ROLE_STYLE.moderator.name };

function ago(iso) {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

// Mod Activity Log — host + stream Admin see who kicked/banned/denied whom, live.
export default function LiveModActivityLog({ streamId, enabled }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(true);
  const load = useCallback(async () => {
    if (!enabled || !streamId) return;
    try { const { data } = await api.get(`/live/${streamId}/mod-log`, { params: { limit: 50 } }); setEntries(data?.entries || []); } catch { /* ignore */ }
  }, [enabled, streamId]);
  useEffect(() => { if (enabled) load(); }, [enabled, streamId, load]);
  useLiveRoomEvents((msg) => {
    if (msg?.channel === "moderation:log" && msg.entry) setEntries((prev) => prev.some((e) => e.id === msg.entry.id) ? prev : [msg.entry, ...prev].slice(0, 50));
  }, { enabled: !!enabled });
  if (!enabled) return null;
  return (
    <div className="mt-4 border border-white/10 rounded-md" data-testid="live-mod-log">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="live-mod-log-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
        <span className="flex items-center gap-1.5"><ScrollText size={12} /> Mod activity {entries.length ? `· ${entries.length}` : ""}</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <ul className="max-h-48 overflow-y-auto divide-y divide-white/5 px-3 pb-2" data-testid="live-mod-log-list">
          {entries.length === 0 && <li className="py-3 text-xs text-zinc-500" data-testid="live-mod-log-empty">No moderation actions yet this Live.</li>}
          {entries.map((e) => (
            <li key={e.id} className="py-1.5 text-[11px] leading-snug text-zinc-300" data-testid={`live-mod-log-entry-${e.id}`}>
              <span className={`font-semibold ${ROLE_TXT[e.actor_role] || "text-white"}`}>{e.actor_name}</span>
              {e.actor_role && e.actor_role !== "host" && <span className="ml-1 text-[8px] uppercase tracking-widest text-zinc-500">{e.actor_role}</span>}
              <span className="text-zinc-400"> {e.verb} </span>
              {e.target_name && <span className="text-white">{e.target_name}</span>}
              {e.detail && <span className="text-zinc-500"> · “{e.detail}”</span>}
              <span className="text-zinc-600"> · {ago(e.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
